import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ReleaseNotesService } from '../../services/master';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { HasPermissionService } from 'src/app/services/common/has-permission.service';

@Component({
  standalone: false,
  selector: 'app-whats-new-add',
  templateUrl: './whats-new-add.component.html',
  styleUrls: ['./whats-new-add.component.scss']
})
export class WhatsNewAddComponent implements OnInit {
  releaseNoteForm: FormGroup;
  today = new Date().toISOString().split('T')[0];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private releseNoteService: ReleaseNotesService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private permission: HasPermissionService,
  ) {}

  ngOnInit(): void {
    this.releaseNoteForm = this.fb.group({
      releaseNoteLabel: ['', Validators.required],
      initialDate: ['', Validators.required],
      endDate: ['', Validators.required],
      isActive: [true],
      releaseNotes: this.fb.array([this.buildNote()])
    });
  }

  get releaseNotes(): FormArray {
    return this.releaseNoteForm.get('releaseNotes') as FormArray;
  }

  private buildNote(text: string = '', isHeading: boolean = false) {
    return this.fb.group({
      text: [text, Validators.required],
      isHeading: [isHeading]
    });
  }

  addNoteField(): void {
    this.releaseNotes.push(this.buildNote());
  }

  removeNoteField(index: number): void {
    this.releaseNotes.removeAt(index);
  }

  back(): void {
    this.router.navigate(['/whats-new-list']);
  }

  save(): void {
    if (this.releaseNoteForm.invalid) return;

    const formValue = this.releaseNoteForm.value;

    const notesAsStrings: string[] = (formValue.releaseNotes || []).map((n: any) => {
      const txt = String(n?.text ?? '');
      return n?.isHeading ? `<strong>${txt}</strong>` : txt;
    });

    const payload = {
      initialDate: formValue.initialDate,
      endDate: formValue.endDate,
      releaseNoteLabel: formValue.releaseNoteLabel,
      releaseNote: JSON.stringify({ ReleaseNotes: notesAsStrings }),
      isActive: formValue.isActive
    };

    this.releseNoteService.apiReleaseNotesAddReleaseNotesPost(payload).subscribe({
      next: () => {
        this.toastr.success(this.translate.instant('Action.Success'));
        this.router.navigate(['/whats-new-list']);
      },
      error: () => {
        this.toastr.error(this.translate.instant('Action.Error'));
      }
    });
  }

  onImportFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const raw = String(reader.result || '');
        const parsed = JSON.parse(raw);

        this.releaseNoteForm.patchValue({
          releaseNoteLabel: parsed.releaseNoteLabel ?? '',
          initialDate: parsed.initialDate ?? '',
          endDate: parsed.endDate ?? '',
          isActive: typeof parsed.isActive === 'boolean' ? parsed.isActive : true
        });

        let rnPayload: any = parsed.releaseNote;
        if (typeof rnPayload === 'string') {
          try { rnPayload = JSON.parse(rnPayload); } catch { rnPayload = null; }
        }
        const arr = Array.isArray(rnPayload?.ReleaseNotes) ? rnPayload.ReleaseNotes : [];

        while (this.releaseNotes.length) this.releaseNotes.removeAt(0);

        if (arr.length) {
          arr.forEach((item: any) => {
            if (item && typeof item === 'object' && 'text' in item) {
              this.releaseNotes.push(this.buildNote(String(item.text ?? ''), !!item.isHeading));
            } else if (typeof item === 'string') {
              const m = item.match(/^<strong>(.*)<\/strong>$/i);
              if (m) this.releaseNotes.push(this.buildNote(m[1], true));
              else this.releaseNotes.push(this.buildNote(item, false));
            }
          });
        } else {
          this.releaseNotes.push(this.buildNote());
        }

        this.toastr.success(this.translate.instant('Action.Success'));
      } catch {
        this.toastr.error(this.translate.instant('Action.Error'));
      } finally {
        input.value = '';
      }
    };
    reader.readAsText(file);
  }
}